import type { WeeklyProgressResponse, YearlyPerformanceAnalysis } from '../types';

export interface WeeklySeriesResult {
  categories: string[];
  completed: number[];
  pending: number[];
  completionRate: number[];
}

/** Aligned arrays for the weekly progress chart. Null slice → empty chart. */
export function deriveWeeklySeries(weekly: WeeklyProgressResponse | null): WeeklySeriesResult {
  const series = weekly?.series ?? [];
  return {
    categories: series.map((p) => p.label),
    completed: series.map((p) => p.completed),
    pending: series.map((p) => p.pending),
    completionRate: series.map((p) => p.completionRate),
  };
}

export interface MomentumPoint {
  label: string;
  completed: number;
  pending: number;
  skipped: number;
  totalTasks: number;
  completionRate: number;
}

/**
 * Weekly points → chart points for the Momentum board. Optionally filters
 * to a [from, to] window and groups by calendar month (completion rate is
 * recomputed from the grouped sums, not averaged).
 */
export function toMomentumPoints(
  weekly: WeeklyProgressResponse | null,
  opts: { groupBy: 'week' | 'month'; from?: Date; to?: Date }
): MomentumPoint[] {
  let series = weekly?.series ?? [];
  if (opts.from || opts.to) {
    series = series.filter((p) => {
      const d = new Date(p.weekStart);
      if (opts.from && d < opts.from) return false;
      if (opts.to && d > opts.to) return false;
      return true;
    });
  }

  if (opts.groupBy === 'week') {
    return series.map((p) => ({
      label: p.label,
      completed: p.completed,
      pending: p.pending,
      skipped: p.skipped ?? 0,
      totalTasks: p.totalTasks,
      completionRate: Math.round(p.completionRate),
    }));
  }

  const byMonth = new Map<string, MomentumPoint>();
  for (const p of series) {
    const d = new Date(p.weekStart);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('en', { month: 'short', year: '2-digit' });
    const acc = byMonth.get(key) ?? { label, completed: 0, pending: 0, skipped: 0, totalTasks: 0, completionRate: 0 };
    acc.completed += p.completed;
    acc.pending += p.pending;
    acc.skipped += p.skipped ?? 0;
    acc.totalTasks += p.totalTasks;
    byMonth.set(key, acc);
  }
  return [...byMonth.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, p]) => ({
      ...p,
      completionRate: p.totalTasks > 0 ? Math.round((p.completed / p.totalTasks) * 100) : 0,
    }));
}

export type YearlyCategoryKey = keyof YearlyPerformanceAnalysis;

export interface YearlySeriesResult {
  categories: string[];
  actual: number[];
  planned: number[];
}

/** Actual-vs-planned arrays for one yearly performance category. */
export function deriveYearlySeries(
  yearly: YearlyPerformanceAnalysis | null,
  key: YearlyCategoryKey
): YearlySeriesResult {
  const rows = yearly?.[key] ?? [];
  return {
    categories: rows.map((r) => String(r.year)),
    actual: rows.map((r) => r.actual),
    planned: rows.map((r) => r.planned),
  };
}
